import React, { useMemo } from 'react';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Alert, Box, Button, Chip, Tooltip } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { getRegionNamesAndCode } from '@luxuryescapes/lib-regions';

import GridPagination from '~/components/Common/Elements/GridPagination';

import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import type { TagSchedule } from '~/services/TagsService';
import { formatDateSlashes } from '~/services/TimeService';

import { useGetTagSchedules } from '../hooks';

interface TagSchedulesListProps {
  tagId?: string;
  schedules: Array<TagSchedule>;
  onEditClick: (schedule: TagSchedule | TagSchedule) => void;
  onCreateClick: () => void;
}

export default function TagSchedulesList({ tagId, schedules = [], onEditClick, onCreateClick }: TagSchedulesListProps) {
  const { paginationModel, setPaginationModel } = useDataGridPagination({
    pageSize: 25,
    page: 0,
  });

  const regionOptions = useMemo(() => {
    return getRegionNamesAndCode().map((region) => ({
      name: region.name,
      code: region.code,
    }));
  }, []);

  const {
    data = undefined,
    isLoading = false,
    error = null,
  } = useGetTagSchedules(
    tagId
      ? {
          tagId,
          offset: paginationModel.page * paginationModel.pageSize,
          limit: paginationModel.pageSize,
        }
      : undefined,
  );

  const columns: Array<GridColDef<TagSchedule>> = [
    {
      field: 'brands',
      headerName: 'Brands',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<TagSchedule>) => (
        <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" height="100%">
          {params.row.brands.map((brand) => (
            <Chip key={brand} label={brand} size="small" />
          ))}
        </Box>
      ),
    },
    {
      field: 'regions',
      headerName: 'Regions',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<TagSchedule>) => {
        const regions = params.row.regions || [];
        const isAllRegions = regions.length === regionOptions.length;

        if (regions.length === 0) {
          return (
            <Box display="flex" alignItems="center" height="100%">
              N/A
            </Box>
          );
        }

        if (isAllRegions) {
          return (
            <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" height="100%">
              <Chip label="All Regions" size="small" />
            </Box>
          );
        }

        const displayRegions = regions.slice(0, 3);
        const remainingCount = regions.length - 3;
        const remainingRegionNames = regions
          .slice(3)
          .map((code) => regionOptions.find((r) => r.code === code)?.name || code)
          .join(', ');

        return (
          <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" height="100%">
            {displayRegions.map((code) => {
              const region = regionOptions.find((r) => r.code === code);
              return <Chip key={code} label={region?.name || code} size="small" />;
            })}
            {remainingCount > 0 && (
              <Tooltip title={remainingRegionNames}>
                <Chip label={`+${remainingCount} more`} size="small" variant="outlined" />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'activeStartDate',
      headerName: 'Active Period Start',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<TagSchedule>) => (
        <Box display="flex" alignItems="center" height="100%">
          {params.row.activeStartDate ? formatDateSlashes(params.row.activeStartDate) : 'N/A'}
        </Box>
      ),
    },
    {
      field: 'activeEndDate',
      headerName: 'Active Period End',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<TagSchedule>) => (
        <Box display="flex" alignItems="center" height="100%">
          {params.row.activeEndDate ? formatDateSlashes(params.row.activeEndDate) : 'N/A'}
        </Box>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 130,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Edit"
          onClick={() => onEditClick(params.row)}
          color="inherit"
        />,
      ],
    },
  ];

  const allSchedules = tagId
    ? data?.result || []
    : schedules.map((schedule) => ({
        ...schedule,
        tagId: '',
      }));

  return (
    <Box>
      {!!error && <Alert severity="error">{error?.message}</Alert>}
      <Box mb={4} display="flex" justifyContent="flex-end">
        <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateClick}>
          Add Schedule
        </Button>
      </Box>
      <DataGrid
        loading={isLoading && !!tagId}
        rows={allSchedules}
        columns={columns}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowId={(row: TagSchedule) =>
          `${row?.id}-${row?.tagId}-${row?.brands?.join(',')}-${row?.activeStartDate}-${row?.activeEndDate}`
        }
        paginationMode="server"
        rowCount={tagId ? data?.pagination?.total_items : allSchedules.length}
        slots={{
          pagination: GridPagination,
        }}
        autoHeight
        sx={{
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      />
    </Box>
  );
}
